import * as moment from 'moment';
import { Observable, of } from 'rxjs';
import { IMemberListItemContext, IMemberListItemNew } from 'src/app/pages/_searchs/member/member-search.model';
import { Genders } from '../pages/_searchs/member/member-search.model';
import { Utilities } from '../services/utilities';
import { ICommand } from './../services/task/command/command.models';
import { AddressTypeIdsEnum, MemberAddress, PendingAddress } from './address';
import { LabelValueLower } from './keyvalue';
import { Language, Languages } from './language.enum';
import { IPhoneNumber, MemberPhone, PendingPhone } from './phone';
import { PreIdentifiedStatus } from './pre-identified-status.enum';
import { IMemberRelationship } from './relationship';
import { ISmartIconSettings, SmartIconSettings } from './smart-icons-settings';
import { TelephonyMemberGroupHashData, TelephonyMemberItem } from './telephonyv2';


export class MemberContext {

    member: Member;
    overview: MemberContextOverview;
    commands: ICommand[];
    metadata: IMemberContextMetadata;
    notifications: MemberNotification[];
    guardScript: string;
    guardNotification: any;
    memberKeyValue: any;
    smsConsentComponent: any;
    mmr: MMRRecord;
    relationships: IMemberRelationship[];
    taskOrchestrator?: IMemberContextTaskOrchestrator;
    dashboard: any;
    productDashboard: { [key: string]: ISmartIconSettings[] } = undefined; // Default mock data for product dashboard

    customerService: ICustomerService;
    allowCustomDocumentComposition: boolean = true;

    constructor(input: Partial<MemberContext>, member?: Member) {

        Object.assign(this, input);

        this.member = new Member(input.member);
        this.overview = new MemberContextOverview(input && input.overview, (member || this.member));

    }
}

export interface IMemberContextMetadata {
    onCall: boolean;
    verified: boolean;
    sessionStatus?: SessionStatus;
    skillExternalId: string;
    skillId: number;
    sessionReasonTypes: (Origin & Reason)[];
    greetingTaskCompleted?: boolean;
}

export interface Origin { id: number, externalId: string, displayName: string }
export interface Reason { id: number, externalId: string, description: string }


export enum SessionStatus {
    Open = 'open',
    Closing = 'closing',
    Closed = 'closed'
}

export class MemberContextOverview {
    tenantDisplayName: string;
    color: string;
    status: string;

    constructor(input: Partial<MemberContextOverview>, member: Member) {
        if (input != null) {
            Object.assign(this, input);
        } else {
            this.defaultContext(member);

        }
    }


    defaultContext(member: Member) {
        // false means active
        if (member.tenantMemberStatus == false) {
            switch (member.memberType) {
                case 'C':
                    this.status = 'change';
                    break;
                case 'S':
                    this.status = 'suspend';
                    break;
                case 'UHG':
                    this.status = 'uhg';
                    break;
                default:
                    this.status = 'white';
                    break;
            }
        } else {
            this.status = 'inactive';
        }

        this.status = this.status || 'change';

    }
}


export interface MemberNotification {
    level: string;
    header: string;
    details: string;
    icon: string;
    dismissible: boolean;
    category: 'member-task';
    data: any;
}
export enum SSNViewMode {
    Edit = 'edit',
    ViewLastOnly = 'viewlastonly',
    ViewFull = 'viewfull',
    None = 'none',
}

export class MemberSSN {
    memberId: string;
    ssn: string;
}

export class Member {
    id?: string;
    mbi?: string;
    cardNumber: string;
    hContract: string;
    pBP?: string;
    hicn?: string;

    source?: string;
    dateOfBirth: Date | string;
    // gender: Gender;
    gender: string;
    language: Language | string;
    firstName: string;
    middleInitial: string;
    lastName: string;
    emailAddress: string;
    allowMail?: boolean;
    allowTelephony?: boolean;
    stateTo?: string;
    calculatedScore?: number;
    sourceFileType?: string;
    riskScore?: number;
    preIdentifiedStatus?: PreIdentifiedStatus;
    masterGroup?: string;
    isDualSpecialNeedsProgram?: boolean = null;
    primaryAddress?: MemberAddress;
    primaryPhone?: IPhoneNumber;
    fullName?: string;
    daysToBirthday?: number;
    originType?: string;
    memberType?: string;
    isPotentialVeteran?: boolean;
    isVeteran?: boolean;
    isDeceased?: boolean;
    doNotSolicit?: boolean;
    tenantMemberStatus?: boolean;
    createdOn?: Date;
    relations: PersonRelations;
    hasSSN: boolean;
    verifiedOn?: Date;
    verifiedBy?: string;

    addresses: MemberAddress[];
    pendingAddresses: PendingAddress[];
    addressesHistory: MemberAddress[];
    phones: MemberPhone[];
    pendingPhones: PendingPhone[];
    activities: any[] = [];
    isSms?: boolean;

    $memberHash: string;
    $memberHashSource: TelephonyMemberItem[];

    pbp: string;
    tenantName: string;

    get base64TenantId() {
        if (this.base64) {
            const parts = Utilities.splitInTwo(atob(this.base64), '|');
            return parts.firstPart.toUpperCase();
        }
    }
    externalId?: string;
    base64: string;

    constructor(input: Partial<Member>) {

        Object.assign(this, input);

        this.relations = new PersonRelations(input.relations);
        // TODO: Clear

        if (!this.id && this.base64) {
            const parts = Utilities.splitInTwo(atob(this.base64), '|');
            this.id = Utilities.splitInTwo(parts.secondPart, '|').firstPart;
        }


        let gender: LabelValueLower;
        if (input.gender) {
            gender = Genders.find(item => item.value === input.gender || item.label.toUpperCase() === input.gender.toUpperCase());
        }
        const language = Languages.find(item => input.language && (item.id === input.language.toString() || item.name === input.language.toString()));

        this.language = language && language.id;
        this.gender = gender && gender.value;

        this.addresses = (input.addresses as any[] || []).map(memberAddress => new MemberAddress(memberAddress));
        this.phones = (input.phones as any[] || []).map(memberPhone => new MemberPhone(memberPhone));
        this.pendingAddresses = (input.pendingAddresses || []).map(pendingAddress => {
            let address = new PendingAddress();
            Object.assign(address, pendingAddress);
            return address;
        });

        this.pendingPhones = (input.pendingPhones || []).map(pending => {
            let ph = new PendingPhone();
            Object.assign(ph, pending);
            return ph;
        });

        this.getAddressByType(AddressTypeIdsEnum.Home, true).subscribe((data: MemberAddress) => {
            this.primaryAddress = data;
        });

        this.primaryPhone = this.phones.find(_ => _.isPrimary) || this.phones[0];

        this.fullName = Member.getFullName(this);
        const today = moment();
        const year = today.year();
        const yearBirthday = moment(this.dateOfBirth).year(year);
        if (yearBirthday.isBefore(today)) {
            yearBirthday.year(year + 1);
        }

        const ms = yearBirthday.diff(today);
        this.daysToBirthday = Math.abs(Math.ceil(moment.duration(ms).asDays()));

    }

    getAddressByType(preferredType: AddressTypeIdsEnum, useFallback: boolean = false): Observable<MemberAddress> {
        const addresses = this.addresses;

        // Check if Home is also a Mailing address
        const homeIsMailingAddress = addresses.some(address =>
            address.type === AddressTypeIdsEnum.Home && address.isMailingAddress === true
        );

        // If preferred type is Mailing and Home is a Mailing address, return the Home address
        if (preferredType === AddressTypeIdsEnum.Mailing && homeIsMailingAddress) {
            const homeAddress = addresses.find(a => a.type === AddressTypeIdsEnum.Home && a.isMailingAddress === true);
            return of(homeAddress);
        }

        // Find the address of the preferred type
        const foundAddress = addresses.find(a => a.type === preferredType);

        if (foundAddress || !useFallback) {
            return of(foundAddress || null);
        }
        // Find the fallback address of the preferred type
        const fallbackAddress = addresses.find(a => {
            const fallbackType = this.getFallbackType(preferredType);
            return a.type === fallbackType &&
                !addresses.some(b => b.memberId === a.memberId && b.type === preferredType);
        });

        return of(fallbackAddress || null);
    }

    getFallbackType(preferredType: AddressTypeIdsEnum): AddressTypeIdsEnum {
        switch (preferredType) {
            case AddressTypeIdsEnum.Home: return AddressTypeIdsEnum.Mailing;
            case AddressTypeIdsEnum.Mailing: return AddressTypeIdsEnum.Home;
            case AddressTypeIdsEnum.Other: return AddressTypeIdsEnum.Home;
            default: return preferredType;
        }
    }

    static getFullName(member: { firstName: string, middleInitial?: string, lastName: string }) {
        const middleInitial = ((member.middleInitial?.trim() ?? '') != '') ? ` ${member.middleInitial} ` : ' ';
        return `${member.firstName}${middleInitial}${member.lastName}`;
    }

    static getMemberHash(member: Member): TelephonyMemberGroupHashData {
        let relations: PersonRelations = member.relations;
        if (relations == null || (relations instanceof PersonRelations) == false) {
            relations = new PersonRelations(relations);
        }
        let members = relations.members.map(memberItem => ({ id: memberItem.id }));

        // apply distint
        members = members.filter((item, index) => members.findIndex(indexItem => indexItem.id === item.id) === index);
        members.push({ id: member.id });
        const orderedMembers = members.sort((a, b) => a.id < b.id ? -1 : (a.id > b.id ? 1 : 0));
        const stringfy = JSON.stringify(orderedMembers);
        const result = btoa(stringfy);
        return { memberGroupHash: result, memberGroup: orderedMembers } as TelephonyMemberGroupHashData;
    }

    static formatFromResponsibleParty(responsibleParty: IMemberListItemContext) {

        return new MemberContext({
            member: new Member({
                id: responsibleParty.item.Id,
                mbi: responsibleParty.item.MBI,
                cardNumber: responsibleParty.item.CardNumber,
                // hContract: this.responsibleParty.item.,
                pBP: responsibleParty.item.PBP,
                dateOfBirth: responsibleParty.item.DateOfBirth,
                // gender: Gender;
                // gender: this.responsibleParty.item.,
                // language: this.responsibleParty.item.Id,
                firstName: responsibleParty.item.FirstName,
                middleInitial: responsibleParty.item.MiddleInitial,
                lastName: responsibleParty.item.LastName,
                emailAddress: responsibleParty.item.EmailAddress,
                addresses: responsibleParty.item.Addresses?.map(addressItem => new MemberAddress({
                    id: undefined,
                    memberId: responsibleParty.item.Id,
                    type: undefined,
                    street1: addressItem.Street1,
                    street2: addressItem.Street2,
                    city: addressItem.City,
                    state: addressItem.State,
                    postalCode: addressItem.PostalCode,
                    source: undefined
                })),
                phones: responsibleParty.item.Phones?.map(phoneItem => new MemberPhone({
                    memberId: responsibleParty.item.Id,
                    number: phoneItem.Number,
                }))
            })
        });

    }


    static formatFromPersonRelation(relation: IPersonRelation) {

        return new MemberContext({
            member: new Member({
                id: relation.id,
                mbi: relation.mbi,
                cardNumber: relation.cardNumber,
                pBP: relation.pbp,
                dateOfBirth: relation.dateOfBirth,
                firstName: relation.firstName,
                middleInitial: relation.middleInitial,
                lastName: relation.lastName,
                emailAddress: relation.emailAddress,
                addresses: relation.addresses?.map(addressItem => new MemberAddress({
                    memberId: relation.id,
                    street1: addressItem.street1,
                    street2: addressItem.street2,
                    city: addressItem.city,
                    state: addressItem.state,
                    postalCode: addressItem.postalCode,
                })),
                phones: relation.phones?.map(phoneItem => new MemberPhone({
                    id: phoneItem.id,
                    memberId: relation.id,
                    number: phoneItem.number,
                    isPrimary: phoneItem.isPrimary,
                }))
            })
        });
    }
}


export class MemberFacility {

}


export class MMRRecord {
    contractNumber: string;
    memberId: string;
    // public runDate: Date;
    //  public paymentDate: Date;
    // public ageGroup: string;
    // public stateAndCountryCode: string;
    // public outOfAreaIndicator: boolean;
    // public partAEntitlement: boolean;
    // public partBEntitlement: boolean;
    hospice: boolean;
    // public ESRD: boolean;
    esrd: boolean;
    // public agedDisabledMSP: boolean;
    // public institutional: boolean;
    // public NHC: boolean;
    // public newMedicareBeneficiaryMedicaidStatus: string;
    // public LTI: boolean;
    lti: boolean;
    // public medicaidIndicator: string;
    // public MMRAdjustmentReasonCode: string;
    // public paymentAdjustmentStartDate: Date;
    // public paymentAdjustmentEndDate: Date;
    // public LISPremiumSubsidy: number;
    medicaidStatus: string;
    medicaidType: string;
    medicaidProductType: string;
    // public dualAndPartDEnrollmentStatus: string;
    // public PBP: string;
    pbp: string;
    // public raceCode: string;
    // public riskAdjustmentFactorTypeCode: string;
    // public EGHP: boolean;
    eghp: boolean;
    // dualStatusCodeId: number;
    dualStatusCode: any;
    // public createdOn: Date;
    lastModifiedOn: Date;

    medicaidStatusStartDate: Date;
    medicaidStatusEndDate: Date;
    lis: boolean;
    orec: any;
    calculatedRecertDate: Date;
    redeterminationDate: Date;
    basicPremiumAmountPartD: string;
}

export interface IConvertMemberRequest {
    originTypeId: number;
    createdBy: string;
    member: IMemberListItemNew;
}

export class MemberRequestModel {
    ingestionOrchestrationId: string;
    member: MemberRequestItem;
    addresses: MemberAddress[];
    phoneNumbers: MemberPhone[];
    facilities: MemberFacility[];

    constructor(ingestionOrchestrationId: string, member: MemberRequestItem, addresses: MemberAddress[], phoneNumbers: MemberPhone[], facilities: MemberFacility[]) {

        this.ingestionOrchestrationId = ingestionOrchestrationId;
        this.member = member;
        this.addresses = addresses;
        this.phoneNumbers = phoneNumbers;
    }
}

export class MemberSSNRequestItem {
    memberId: string;
    ssn: string;

    constructor(memberId: string, ssn: string) {
        this.memberId = memberId;
        this.ssn = ssn;
    }

}

export class MemberRequestItem {

    id: string;
    mbi: string;
    source: string;
    dateOfBirth: string;
    gender: string;
    language: string;
    firstName: string;
    middleInitial: string;
    lastName: string;
    emailAddress: string;
    originType: number;
    isVeteran?: boolean;
    isDeceased?: boolean;
    doNotSolicit?: boolean;
    sourceFileType: string;
    isSms?: boolean; // saved in member's property
    tenantMemberStatus?: boolean;
    verifiedOn?: Date;
    verifiedBy?: string;

    constructor(obj: any)
    constructor(fistName: string, lastName: string, mbi: string, gender: string, dateOfBirth: string, originType: number, emailAddress: string, id?: string, language?: string, isVeteran?: boolean, isDeceased?: boolean, doNotSolicit?: boolean, middleInitial?: string, isSms?: boolean)
    constructor(firstNameOrObject: any, lastName?: string, mbi?: string, gender?: string, dateOfBirth?: string, originType?: number, emailAddress?: string, id?: string, language?: string, isVeteran?: boolean, isDeceased?: boolean, doNotSolicit?: boolean, middleInitial?: string, isSms?: boolean, tenantMemberStatus?: boolean, verifiedOn?: Date, verifiedBy?: string) {
        if (typeof firstNameOrObject === 'string') {
            this.id = id;
            this.firstName = firstNameOrObject as string;
            this.lastName = lastName;
            this.mbi = mbi;
            this.dateOfBirth = dateOfBirth;
            this.gender = gender;
            this.originType = originType;
            this.emailAddress = emailAddress;
            this.language = language;
            this.middleInitial = middleInitial;
            this.isVeteran = isVeteran;
            this.isDeceased = isDeceased;
            this.doNotSolicit = doNotSolicit;
            this.tenantMemberStatus = tenantMemberStatus;
            this.isSms = isSms;
            this.verifiedOn = verifiedOn;
            this.verifiedBy = verifiedBy;
        } else {
            const internal = (firstNameOrObject || {}) as MemberRequestItem;
            this.id = internal.id;
            this.firstName = internal.firstName;
            this.lastName = internal.lastName;
            this.mbi = internal.mbi;
            this.dateOfBirth = internal.dateOfBirth;
            this.gender = internal.gender;
            this.originType = internal.originType;
            this.emailAddress = internal.emailAddress;
            this.language = internal.language;
            this.middleInitial = internal.middleInitial;
            this.isVeteran = internal.isVeteran;
            this.isDeceased = internal.isDeceased;
            this.doNotSolicit = internal.doNotSolicit;
            this.isSms = internal.isSms;
            this.tenantMemberStatus = internal.tenantMemberStatus;
            this.verifiedOn = internal.verifiedOn;
            this.verifiedBy = internal.verifiedBy;
        }
    }
}


export class MemberRequestResponse {
    memberId: string;
    member: MemberRequestItem;
    error: string;
}


export class PersonRelations extends Array<IPersonRelation> {
    constructor(input?: IPersonRelation[]) {
        super();

        if (input && Array.isArray(input)) {
            this.push(...input);
        }
    }

    get members() {
        return this.filter(relation => relation.source === 'Member');
    }

    get dependents() {
        return this.filter(relation => relation.type === RelationType.Dependent);
    }

    get relations() {
        return this.filter(relation => relation.type === RelationType.Relation);
    }

    get responsibleParty(): IPersonRelation {
        return this.find(relation => relation.type === RelationType.ResponsibleParty);
    }
}
export interface IPersonRelation {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: Date;
    source: PersonRelationSource;
    type?: RelationType;
    typeDisplayName?: string;
    addresses: {
        street1: string,
        street2: string,
        city: string,
        state: string,
        postalCode: string
    }[],
    phones: {
        id: number;
        isPrimary?: boolean;
        number: string;
    }[],
    cardNumber: string,
    contractNumber: string,
    emailAddress: string,
    mbi: string;
    middleInitial: string;
    pbp: string;
    repository: string;
    tenantId: string;
}

export interface ICustomerService {
    phoneNumbers: ICustomerServicePhoneNumber[]
}

export interface ICustomerServicePhoneNumber {

    title: string;
    number: string;
    extension: string;
    isBenelynkPhoneNumber: boolean;
    options: any;
    hoursOfOperation: IHourOfOperation[];

    $displayFormat: any;
    $todayHoursOfOperation: IHourOfOperation;
    $extraDaysHoursOfOperation: IHourOfOperation[];
    $hoursOfOperationOpened: boolean;
}

export interface IHourOfOperation {
    isOpen?: boolean;
    dayOfWeek: string;
    start?: string;
    end?: string;
    timeZone?: string;

    $timeZone?: ITimeZone
    $timeZones?: { [timeZoneReference: string]: { timeZone: ITimeZone, start: string, end: string } };
}

export interface ITimeZone {
    id: string; symbol: string; reference: string;
}

export enum PersonRelationSource {
    Member = 'Member',
    ResponsibleParty = 'ResponsibleParty'
}

export enum RelationType {
    ResponsibleParty,
    Dependent,
    Relation
}


export interface IMemberProductLazyMetadata {
    member: MemberContext;
    options: IMemberProductLazyOptionsMetadata;
}

export interface IMemberProductLazyOptionsMetadata {
    disableNavigation: boolean;
}


export interface ISubmittable {
    onSubmit();
}


export interface IMemberContextTaskOrchestrator {
    contextName: string;
}
